import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import {
  ServicesURL,
  downloadFile,
  getQueryString,
  uploadFile,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { ActionsType, ColumnsType, InputType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { User } from "src/interfaces/User";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ButtonBackPage from "@components/ButtonBackPage";
import AnimationFadeIn from "@components/AnimationFadeIn";
import AddCustomer from "./AddCustomer";
import AddUser from "@pages/Contents/DigitedUsers/AddUser";
import EditDigitedUsers from "@pages/Contents/DigitedUsers/EditDigitedUsers";
import EditCustomer from "./EditCustomer";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useHeader } from "@context/header";
import { getCookie } from "@utils/utilsCookie";
import { handleSwitchToLearner } from "@utils/utilsCognito";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { useFilters } from "@context/filters";
import dayjs from "dayjs";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import { useAsyncProcess } from "@hooks/useAsyncProcess";
import AsyncProcessBanner from "@components/AsyncProcessBanner";
import { AsyncProcessStatusEnum } from "src/interfaces/AsyncProcess";

export const RedirectAddEditUser: React.FC<{ isAdd: boolean }> = ({
  isAdd,
}) => {
  const { id } = useParams();
  if (id === "1") {
    return isAdd ? <AddUser /> : <EditDigitedUsers />;
  } else {
    return isAdd ? <AddCustomer /> : <EditCustomer />;
  }
};

const Customers: React.FC = () => {
  const { t, i18n } = useTranslation(["customers-page", "modal"]);
  const { id } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.users"]}`;

  const { setHideHeader } = useHeader();
  const sessionId = getCookie("sessionId") || "";
  const navigate = useNavigate();

  // START formattazione dei filtri in base alle esigenze del backend
  let newRoles = {};
  if (filters[filterId]?.roles?.length > 0) {
    newRoles = {
      roles: filters[filterId].roles
        ?.map((type: any) => {
          return (type?.value).toString().toUpperCase();
        })
        .join(","),
    };
  }
  let newBirthDate = {};
  if (filters[filterId]?.birthday) {
    newBirthDate = { birthday: dayjs(filters[filterId]?.birthday).valueOf() };
  }
  let newHireDate = {};
  if (filters[filterId]?.hire_date) {
    newHireDate = { hire_date: dayjs(filters[filterId]?.hire_date).valueOf() };
  }
  // END formattazione dei filtri in base alle esigenze del backend

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable<User[]>(
      `${ServicesURL.getCustomer}?corporate_id=${id}`,
      PagesID["corporate.customers"],
      filters[filterId]
        ? { ...filters[filterId], ...newRoles, ...newBirthDate, ...newHireDate }
        : { pageIndex: 0, pageSize: 20 },
    );

  const { trigger: getTemplatePresignedUsers } = useSWRMutation(
    `${ServicesURL.getCustomersDownloadV3}?corporate_id=${id}&type=LEARNER`,
    (url: string) => fetcher(`${url}`, MethodType.GET),
  );

  const { columns: columnsCustomFields, dataDetail: dataCustomFields } =
    useFetchDataDetail(
      `${ServicesURL.getCorporateCustomFields}?corporate_id=${id}`,
      PagesID["corporate.customFields"],
    );
  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID["corporate.customers"],
  );
  const { trigger: triggerChangeStatus } = useSWRMutation(
    `${ServicesURL.putCustomerStatus}`,
    (
      url: string,
      {
        arg,
      }: { arg: { status: string; users_id: string; corporate_id: string } },
    ) => {
      return fetcher<any>(
        getQueryString(`${url}/${arg.status}`, {
          users_id: arg.users_id,
          corporate_id: arg.corporate_id,
        }),
        MethodType.PUT,
      );
    },
  );
  const { trigger: triggerDeleteUser } = useSWRMutation(
    `${ServicesURL.deleteCustomer}`,
    (
      url: string,
      { arg }: { arg: { users_id: string; corporate_id: string } },
    ) => {
      return fetcher<any>(
        getQueryString(`${url}`, {
          users_id: arg.users_id,
          corporate_id: arg.corporate_id,
        }),
        MethodType.DELETE,
      );
    },
  );

  const { trigger: getImpersonationSession } = useSWRMutation(
    ServicesURL.startImpersonation,
    (
      url: string,
      arg: {
        arg: { sessionId: string; corporateId: number; externalId: string };
      },
    ) =>
      fetcher(
        url
          .replace("{sessionId}", sessionId)
          .replace("{corporateId}", `${arg.arg.corporateId}`)
          .replace("{externalId}", arg.arg.externalId),
        MethodType.GET,
      ),
  );

  const { trigger: triggerOnBoardingWelccomeMail } = useSWRMutation(
    id ? `${ServicesURL.postWelcomemail}?corporate_id=${id}` : null,
    (url: string) => fetcher<any>(url, MethodType.POST),
  );

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openStatusUser, setOpenStatusUser] = useState<User>();
  const [openActivateUser, setOpenActivateUser] = useState<User>();
  const [columnsMerged, setColumnsMerged] = useState<ColumnsType[]>();
  const [idUserDeleted, setIdUserDeleted] = useState<string>();
  const [userToImpersonate, setUserToImpersonate] = useState<User | null>();
  const [openLoginAsModal, setOpenLoginAsModal] = useState<boolean>(false);
  const [openModalWelcomeMail, setOpenModalWelcomeMail] =
    useState<ActionsType>();

  const {
    currentProcess,
    isUploadInProgress,
    progressPercentage,
    startAsyncUpload,
    downloadDiscardsCsv,
    dismissProcess,
  } = useAsyncProcess({
    processType: "USER_UPLOAD",
    corporateId: id,
    onCompleted: () => {
      mutate && mutate();
    },
  });

  useEffect(() => {
    if (!columnsMerged) {
      if (columns && dataCustomFields) {
        setColumnsMerged([
          ...(columns.map((el: ColumnsType) => {
            switch (el.accessorKey) {
              // case "roles": {
              //   return {
              //     ...el,
              //     type: InputType.multiCheckbox,
              //     optionsKey: id === "1" ? "roles-digited" : "roles",
              //   };
              // }
              case "uog": {
                return {
                  ...el,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    const uog: any = value.map((uogSingle: any) => {
                      return uogSingle.uog;
                    });
                    return uog.join(", ");
                  },
                };
              }
              case "job": {
                return {
                  ...el,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    return value && value?.label;
                  },
                };
              }
              case "uog_description": {
                return {
                  ...el,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    const uog_description = value && value[0]?.uog_description;
                    return uog_description;
                  },
                };
              }
              default:
                return el;
            }
          }) || []),
          ...(columnsCustomFields && dataCustomFields.catalog
            ? columnsCustomFields
                .filter((el) => dataCustomFields.catalog[el.accessorKey])
                .map((el) => ({
                  ...el,
                  ...{
                    header:
                      dataCustomFields.catalog[el.accessorKey] || el.header,
                  },
                }))
            : []),
        ]);
      }
    }
  }, [columns, columnsCustomFields, columnsMerged, dataCustomFields]);

  const handleUserBlockUnblock = React.useCallback(
    async (row: User) => {
      const newStatus = row.status === "active" ? "SUSPENDED" : "ACTIVE";
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        const res = await triggerChangeStatus({
          status: newStatus,
          users_id: row.id_user.toString(),
          corporate_id: id || "",
        });

        const newRow = {
          ...row,
          ...{ status: row.status === "active" ? "suspended" : "active" },
        };
        const index = dataTable.output.findIndex(
          (el: User) => el.id_user === newRow.id_user,
        );
        const newData = [...dataTable.output];
        newData[index] = newRow;
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setOpenToast(undefined);
        console.error(error);
      }
    },
    [dataTable, id, mutate, triggerChangeStatus],
  );

  const handleUserActivate = React.useCallback(
    async (row: User) => {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        const res = await triggerChangeStatus({
          status: "ACTIVE",
          users_id: row.id_user.toString(),
          corporate_id: id || "",
        });

        const newRow = {
          ...row,
          ...{ status: "active" },
        };
        const index = dataTable.output.findIndex(
          (el: User) => el.id_user === newRow.id_user,
        );
        const newData = [...dataTable.output];
        newData[index] = newRow;
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setOpenToast(undefined);
        console.error(error);
      }
    },
    [dataTable, id, mutate, triggerChangeStatus],
  );

  const handleDelete = React.useCallback(async () => {
    if (idUserDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteUser({
          users_id: idUserDeleted,
          corporate_id: id || "",
        });
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idUserDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        console.error(error);
        setOpenToast(undefined);
      }
    }
  }, [dataTable, id, idUserDeleted, triggerDeleteUser]);

  const handleSendWelcomeMail = React.useCallback(async () => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await triggerOnBoardingWelccomeMail();
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      console.error(error);
      setOpenToast(undefined);
    }
  }, [triggerOnBoardingWelccomeMail]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "button": {
          if (action.key === "activate") {
            return {
              ...action,
              callBack: setOpenActivateUser,
              getIcon: (row: User) => {
                return <PersonAddIcon />;
              },
            };
          } else if (action.key === "login_as") {
            return {
              ...action,
              callBack: (row: User) => {
                setUserToImpersonate(row);
                setOpenLoginAsModal(true);
              },
            };
          } else {
            return {
              ...action,
              callBack: setOpenStatusUser,
              getIcon: (row: User) => {
                if (row.status !== "active") {
                  return <LockIcon />;
                } else {
                  return <LockOpenIcon />;
                }
              },
            };
          }
        }
        case "link": {
          return {
            ...action,
            callBack: (row: any) => navigate(`${row?.id_user}/enrolled-to`),
          };
        }
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => setIdUserDeleted(id),
          };
        }
        case "upload": {
          return {
            ...action,
            callBack: (currentElement: any) => {
              const upload = {
                ...action,
                ...{
                  // service: `${ServicesURL.library}/userprofile/image?userId=${currentElement.id_user}`,
                  types: ["upload", "download"],
                  onDownload: (el: string) => {
                    try {
                      // add true, to indicate that is profile image request. It's optional attribute
                      downloadFile(
                        `${ServicesURL.library}/userprofile/${currentElement.id_user}/image`,
                        true,
                      );
                    } catch (error) {
                      console.error(error);
                    }
                  },
                  onUpload: (formData: FormData) =>
                    uploadFile(
                      `${ServicesURL.library}/userprofile/image?userId=${currentElement.id_user}`,
                      formData,
                    ),
                },
              };
              setOpenUpload(upload);
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionForRow]);

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      const extrasystemWelcome = dataDetail.welcome_email_extrasystem_flag;
      const mailCallBack = extrasystemWelcome
        ? undefined
        : () => setOpenModalWelcomeMail(action);

      switch (action.type) {
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=${id}`,
              types: ["upload", "download", "template"],
              onDownload: async (el) => {
                if (el === "template") {
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/download/template?corporate_id=${id}&type=LEARNER` ||
                      "",
                  );
                } else {
                  try {
                    let response: string = await getTemplatePresignedUsers();
                    if (response) downloadFile(response);
                  } catch (error) {
                    console.error(error);
                  }
                }
              },
              onUpload: (formData: FormData) => {
                if (action.type === "upload") {
                  return startAsyncUpload(formData);
                }
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=${id}`,
                  formData,
                );
              },
              uploadSuccessMessage:
                action.type === "upload"
                  ? (t("async-upload-started", {
                      ns: "async-process",
                    }) as string)
                  : undefined,
            },
          };
          return {
            ...action,
            ...{
              callBack: () => setOpenUpload(actionModal),
              disabled: action.type === "upload" ? isUploadInProgress : false,
            },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=${id}`,
              types: ["upload", "download", "template"],
              onDownload: (el) => {
                if (el === "template") {
                  downloadFile(
                    `${ServicesURL[action.service as KeyURL]}/template` || "",
                  );
                } else {
                  downloadFile(
                    `${
                      ServicesURL[action.service as KeyURL]
                    }/?corporate_id=${id}` || "",
                  );
                }
              },
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=${id}`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "button": {
          return {
            ...action,
            ...{
              getIcon: () => <MailOutlineIcon />,
              callBack: mailCallBack,
              permission: "usermgmt.users.add",
            },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar, id, isUploadInProgress, startAsyncUpload]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    const isPO = user?.roles.includes(RolesDigitedEnum.PRODUCT_OWNER);
    const hasInitiatives =
      row?.original?.initiatives && row.original.initiatives.length > 0;
    const hasLoginAsPermission =
      !!user?.permissions.includes("usermgmt.loginas");
    const canLoginAs = hasInitiatives && (isPO || hasLoginAsPermission);

    configRowNew = configRowNew.map((action: any) => {
      if (action?.key === "login_as") {
        return { ...action, disabled: !canLoginAs };
      } else {
        return { ...action };
      }
    });

    if (
      row.original.status === "active" ||
      row.original.status === "suspended"
    ) {
      return [
        <CustomRowAction
          key={row.id}
          row={row}
          configurationActionForRow={[
            ...configRowNew.filter(
              (el: any) => ["activate"].findIndex((key) => key === el.key) < 0,
            ),
          ]}
          setHideHeader={setHideHeader}
        />,
      ];
    }

    if (row.original.status === "terminated") {
      configRowNew = configRowNew.map((action: any) => {
        if (!action?.key && !["delete", "activate"].includes(action?.key)) {
          return { ...action, disabled: true };
        } else {
          return { ...action };
        }
      });
      return [
        <CustomRowAction
          key={row.id}
          row={row}
          configurationActionForRow={[
            ...configRowNew.filter(
              (el: any) => ["delete"].findIndex((key) => key === el.key) < 0,
            ),
          ]}
          setHideHeader={setHideHeader}
        />,
      ];
    }

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={[
          ...configRowNew.filter(
            (el: any) =>
              ["delete", "activate"].findIndex((key) => key === el.key) < 0,
          ),
        ]}
      />,
    ];
  };

  const loginAs = React.useCallback(async () => {
    if (!userToImpersonate) return;
    await getImpersonationSession({
      sessionId,
      corporateId: userToImpersonate?.corporate_id,
      externalId: userToImpersonate?.external_id,
    });

    user && handleSwitchToLearner(user);
  }, [userToImpersonate]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-users.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataDetail?.name || ""} - ${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        {!columnsMerged ? (
          <FormLoadingStateCst />
        ) : (
          <Box sx={{ mt: "25px" }}>
            <Table
              id={PagesID["corporate.users"]}
              data={dataTable?.output || []}
              columns={columnsMerged || []}
              configurationTopToolbar={actionsToolbar}
              configurationActionForRow={actionForRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              modalTitle={t("hero-title")?.replace("-", "") || ""}
              renderBelowFilters={
                currentProcess ? (
                  <AsyncProcessBanner
                    process={currentProcess}
                    progressPercentage={progressPercentage}
                    onDownloadDiscards={
                      currentProcess.status ===
                      AsyncProcessStatusEnum.COMPLETED_WITH_ERRORS
                        ? () => downloadDiscardsCsv(currentProcess.processUuid)
                        : undefined
                    }
                    onDismiss={dismissProcess}
                  />
                ) : undefined
              }
            />
          </Box>
        )}
      </Container>
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
      <ModalConfirmsCst
        open={!!openStatusUser}
        title={
          openStatusUser?.status === "active"
            ? t("block-user.title", { ns: "modal" })
            : t("unblock-user.title", { ns: "modal" })
        }
        routeToSamePage={true}
        description={
          openStatusUser?.status === "active"
            ? t("block-user.description", { ns: "modal" })
            : t("unblock-user.description", { ns: "modal" })
        }
        onCallBackClosed={() => {
          setOpenStatusUser(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() =>
          openStatusUser && handleUserBlockUnblock(openStatusUser)
        }
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openActivateUser}
        title={t("activate-user.title", { ns: "modal" })}
        routeToSamePage={true}
        description={t("activate-user.description", { ns: "modal" })}
        onCallBackClosed={() => {
          setOpenActivateUser(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() =>
          openActivateUser && handleUserActivate(openActivateUser)
        }
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!idUserDeleted}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setIdUserDeleted(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => idUserDeleted && handleDelete()}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openLoginAsModal}
        title={t("login-as-title", { ns: "modal" })}
        description={t("login-as-description", { ns: "modal" })}
        onCallBackClosed={() => {
          setOpenLoginAsModal(false);
        }}
        status={openToast}
        onActionConfirmed={() => loginAs()}
        navigateUrl="/"
      />
      <ModalConfirmsCst
        open={!!openModalWelcomeMail}
        title={
          openModalWelcomeMail?.labelTranslate
            ? openModalWelcomeMail?.label[i18n.language]
            : ""
        }
        description={t("confirm-welcome-mail.description")}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalWelcomeMail(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={handleSendWelcomeMail}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default Customers;
